import { PopoverOrigin, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import React, { useCallback, useContext, useRef } from 'react';
import { isMobile } from 'react-device-detect';

import { MenuContext } from '../MenuContext';
import { MenuElements } from '../menuElements/MenuElements';
import { useTopLevelMenuStyles } from '../menuStyles/topLevelMenuStyles';

import HeaderEnum from './headerEnum';
import { IconMenuHeader } from './IconMenuHeader';
import { TextMenuHeader } from './TextMenuHeader';
import { topLevelMenuProps } from './topLevelMenuProps';

export function TopLevelMenu(props: topLevelMenuProps) {
  const theme = useTheme();
  const styles = useTopLevelMenuStyles(theme);
  const { selectedHeader, setSelectedHeader } = useContext(MenuContext);

  const {
    id,
    titleEnum,
    alternateTitle,
    children,
    relativePath,
    className,
    menuListClassName,
    showToolTip,
    icon,
    disableRipple,
    hidden = false,
    onClick,
  } = props;

  const defaultShowChevron = icon ? false : !!children;
  const showChevron =
    typeof props?.showChevron !== 'undefined' ? props?.showChevron : defaultShowChevron;

  const labelText: string = alternateTitle ?? titleEnum;
  const titleText = showToolTip ? labelText : null;

  const rootClassName = className
    ? `menu-wrapper ${styles.root} ${className}`
    : `menu-wrapper ${styles.root}`;

  const listClassName = menuListClassName ? `menu-list ${menuListClassName}` : 'topLevelMenu-list';

  const listId = `${props.titleEnum.replace(' ', '-')}-header-menu-container`;

  const anchorOrigin: PopoverOrigin =
    typeof props.anchorOrigin !== 'undefined'
      ? props.anchorOrigin
      : {
          vertical: 'bottom',
          horizontal: 'left',
        };

  const transformOrigin: PopoverOrigin =
    typeof props.transformOrigin !== 'undefined'
      ? props.transformOrigin
      : {
          vertical: 'top',
          horizontal: 'left',
        };

  const open = selectedHeader == props.titleEnum;
  const ariaControls = open ? 'header-topLevelMenu-container' : null;
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = useCallback(
    (event: { type?: string; currentTarget?: unknown }, visible?: boolean) => {
      const currentTargetParent =
        event?.currentTarget instanceof Node ? event.currentTarget.parentNode : undefined;

      if (!isMobile && event.type == 'click' && anchorRef.current == currentTargetParent) {
        return;
      }

      if (relativePath) {
        return;
      }

      if (typeof visible !== 'undefined') {
        setSelectedHeader(visible ? props.titleEnum : HeaderEnum.None);
      } else {
        setSelectedHeader(open ? HeaderEnum.None : props.titleEnum);
      }
    },
    [open, props.titleEnum, relativePath, setSelectedHeader]
  );

  const handleMouseEnter = (event: Event | React.SyntheticEvent) => {
    if (isMobile) {
      return;
    }

    handleToggle(event, true);
  };

  const handleMouseLeave = (event: Event | React.SyntheticEvent) => {
    if (isMobile) {
      return;
    }

    handleToggle(event, false);
  };

  const handleClickAway = (event: Event | React.SyntheticEvent) => {
    const isOpen = selectedHeader == props.titleEnum;
    const isModalClick =
      event.target instanceof Element && event.target.classList.contains('MuiModal-root');

    if (!isOpen || !isModalClick) {
      return;
    }

    handleToggle(event, false);
  };

  return (
    <ClickAwayListener onClickAway={(e) => handleClickAway(e)}>
      <Box
        id={id}
        ref={anchorRef}
        className={rootClassName}
        onMouseLeave={(e) => handleMouseLeave(e)}
        onMouseEnter={(e) => handleMouseEnter(e)}
        onClick={onClick ? onClick : undefined}
        style={{ visibility: hidden ? 'hidden' : 'visible' }}
        data-testid={`top-level-menu-${titleEnum}`}
      >
        {!icon ? (
          <TextMenuHeader
            key={`menu-header-${titleEnum}`}
            ariaControls={ariaControls ?? ''}
            href={relativePath}
            labelText={labelText}
            open={open}
            showChevron={showChevron}
            showToolTip={showToolTip}
            titleText={titleText ?? ''}
            disableRipple={disableRipple}
            handleToggle={(e) => handleToggle(e)}
            data-testid={`text-menu-header-${titleEnum}`}
          />
        ) : (
          <IconMenuHeader
            key={`menu-header-${titleEnum}`}
            ariaControls={ariaControls ?? ''}
            href={relativePath}
            icon={icon}
            labelText={labelText}
            open={open}
            showChevron={showChevron}
            showToolTip={showToolTip}
            titleText={titleText ?? ''}
            disableRipple={disableRipple}
            handleToggle={(e) => handleToggle(e)}
            data-testid={`icon-menu-header-${titleEnum}`}
          />
        )}

        <MenuElements
          id={listId}
          className={styles.menuModal}
          open={open}
          anchorEl={anchorRef.current ?? undefined}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          onClose={(event) => handleToggle(event, false)}
          onClick={(event) => handleClickAway(event)}
          MenuListProps={{
            className: listClassName,
          }}
          PaperProps={{
            className: styles.menuListPaper,
          }}
          data-testid={`menu-elements-${titleEnum}`}
        >
          {children}
        </MenuElements>
      </Box>
    </ClickAwayListener>
  );
}
